import React from 'react';
import { Building2, KeyRound, Lock, Plus, Shield, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAccessTheme } from '../useAccessTheme';
import IdentityProviderModalShell from './IdentityProviderModalShell';
import { FeatureBadgeChip } from './ProviderBadges';
import EntraIcon from './EntraIcon';
import { PROVIDER_CATALOG, type IdentityProviderKind } from './identityProviderTypes';

const PROVIDER_ICONS: Record<Exclude<IdentityProviderKind, 'entra'>, LucideIcon> = {
  'active-directory': Building2,
  okta: Shield,
  oidc: KeyRound,
  saml: Lock,
  scim: Users,
};

interface AddIdentityProviderModalProps {
  onClose: () => void;
  onSelect: (kind: IdentityProviderKind) => void;
}

const AddIdentityProviderModal: React.FC<AddIdentityProviderModalProps> = ({ onClose, onSelect }) => {
  const t = useAccessTheme();

  return (
    <IdentityProviderModalShell
      title="Add Identity Provider"
      subtitle="Choose how your organization manages users and authentication."
      icon={<Plus className="w-5 h-5" />}
      onClose={onClose}
      maxWidthClass="max-w-3xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {PROVIDER_CATALOG.map((item) => {
          const Icon = item.kind === 'entra' ? null : PROVIDER_ICONS[item.kind];
          return (
            <div key={item.kind} className={`rounded-lg border p-4 flex flex-col ${t.tableShell}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${item.kind === 'entra' ? '' : t.iconBg}`}>
                  {item.kind === 'entra' ? <EntraIcon className="w-8 h-8" /> : Icon ? <Icon className="w-5 h-5" /> : null}
                </div>
                <div className="min-w-0">
                  <h4 className={`text-sm font-semibold ${t.title}`}>{item.name}</h4>
                  {item.secondaryLabel ? (
                    <p className={`text-xs mt-0.5 ${t.muted}`}>{item.secondaryLabel}</p>
                  ) : null}
                </div>
              </div>
              <p className={`text-sm mt-3 flex-1 ${t.body}`}>{item.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {item.features.map((feature) => (
                  <FeatureBadgeChip key={feature} label={feature} />
                ))}
              </div>
              <button
                type="button"
                onClick={() => onSelect(item.kind)}
                className={`mt-4 self-start px-3 py-1.5 rounded-md text-sm font-medium ${t.btnPrimary}`}
              >
                {item.actionLabel}
              </button>
            </div>
          );
        })}
      </div>
    </IdentityProviderModalShell>
  );
};

export default AddIdentityProviderModal;
